// @flow

import React, { Fragment } from 'react';

import LdJson from 'web-ssr/app/common/components/LdJson';
import { stripHtmlTagsFromString } from 'web-ssr/app/common/next/utils';
import {
  FACEBOOK_PAGE_ID,
  FACEBOOK_PAGE_SLUG,
  TWITTER_SLUG,
} from 'web-ssr/app/common/constants';

export type SharingMetaTagsType = {
  title?: string,
  description?: string,
  image?: string,
  creator?: string,
  publishedTime?: string,
  modifiedTime?: string,
  url?: string,
  ogType: 'article' | 'website',
  articleSection?: string,
};

const ldJsonOrganization = {
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'Zapier',
  url: 'https://zapier.com',
  legalName: 'Zapier Inc',
  foundingDate: '2011',
  sameAs: [
    `http://www.facebook.com/${FACEBOOK_PAGE_SLUG}`,
    'http://www.linkedin.com/company/zapier/',
    `https://twitter.com/${TWITTER_SLUG}`,
    'http://www.youtube.com/user/ZapierApp/videos',
  ],
  logo: {
    '@type': 'ImageObject',
    url: 'https://zapier.com/brand/assets/images/logos/zapier-logo.png',
  },
  address: {
    addressCountry: 'USA',
    addressLocality: 'San Francisco',
    addressRegion: 'CA',
    streetAddress: '548 Market St. #62411',
    postalCode: '94104',
    '@type': 'PostalAddress',
  },
  founders: [
    {
      '@type': 'Person',
      name: 'Bryan Helmig',
    },
    {
      '@type': 'Person',
      name: 'Wade Foster',
    },
    {
      '@type': 'Person',
      name: 'Mike Knoop',
    },
  ],
  contactPoint: {
    url: 'https://zapier.com/help/',
    email: 'contact@zapier.com',
    '@type': 'ContactPoint',
    contactType: 'customer support',
  },
};

const truncateString = (str: string) => {
  return str.length > 199 ? `${str.substring(0, 199)}...` : str;
};

/**
 * Renders meta tags for social media and search engines.
 *
 * NOTE: Known to be noop when rendered on client, which is fine as bots will
 * get server-side rendered pages, but good to know as it's not spelled out at
 * https://nextjs.org/docs/#populating-head.
 */
const SharingMetaTags = (props: SharingMetaTagsType) => {
  const {
    title,
    description,
    image,
    creator,
    url,
    publishedTime,
    modifiedTime,
    ogType,
    articleSection,
  } = props;

  const sanitizedDescription = description
    ? truncateString(stripHtmlTagsFromString(description))
    : undefined;

  const ldJsonArticle = ogType === 'article' && {
    '@context': 'http://schema.org',
    '@type': 'Article',
    image,
    author: ldJsonOrganization,
    publisher: ldJsonOrganization,
    inLanguage: 'en',
    url,
    description: sanitizedDescription,
    datePublished: publishedTime || undefined,
    dateModified: modifiedTime || undefined,
    headline: title,
    articleSection,
    mainEntityOfPage: url,
  };

  return (
    <Fragment>
      {sanitizedDescription && (
        <meta content={sanitizedDescription} name="description" />
      )}
      {/* Schema.org markup for Google+ */}
      {title && <meta content={title} itemProp="name" />}
      {sanitizedDescription && (
        <meta content={sanitizedDescription} itemProp="description" />
      )}
      {image && <meta content={image} itemProp="image" />}

      {/* Twitter Card data */}
      {title && <meta content={title} name="twitter:title" />}
      {/* Max 199 characters! */}
      {sanitizedDescription && (
        <meta content={sanitizedDescription} name="twitter:description" />
      )}
      {creator && <meta content={creator} name="twitter:creator" />}
      {/* Twitter summary card with large image must be at least 280x150px */}
      {image && <meta content={image} name="twitter:image:src" />}
      {/* TODO: Use `summary` if image is square */}
      <meta content="summary_large_image" name="twitter:card" />
      <meta content={`@${TWITTER_SLUG}`} name="twitter:site" />
      {url && <meta content={url} property="twitter:url" />}

      {/* Open Graph data */}
      {title && <meta content={title} property="og:title" />}
      {url && <meta content={url} property="og:url" />}
      {image && <meta content={image} property="og:image" />}
      {sanitizedDescription && (
        <meta content={sanitizedDescription} property="og:description" />
      )}
      {publishedTime && (
        <meta content={publishedTime} property="article:published_time" />
      )}
      {modifiedTime && (
        <meta content={modifiedTime} property="article:modified_time" />
      )}
      <meta
        content={`https://www.facebook.com/${FACEBOOK_PAGE_SLUG}`}
        property="article:publisher"
      />
      <meta content="Zapier" property="og:site_name" />
      <meta content={ogType} property="og:type" />
      <meta content={FACEBOOK_PAGE_ID} property="fb:page_id" />

      {/* LD+JSON */}
      <LdJson data={ldJsonOrganization} />
      {ldJsonArticle && <LdJson data={ldJsonArticle} />}
    </Fragment>
  );
};

SharingMetaTags.defaultProps = {
  ogType: 'website',
};

export default SharingMetaTags;
